import React, { useRef, useState, useEffect, useMemo } from "react";
import AsyncSelect from "react-select/async";
import classNames from "classnames";

import CustomSelectComponents from "./CustomSelectComponents";

import "./CustomSelect.scss";

export default function CustomAsyncSelect(props: any) {
  const {
    shouldOpenOnFocus,
    forceOpen,
    forceFocus,
    afterMenuOpen,
    onChange,
    isClearable,
    isMulti,
    placeholder,
    isDisabled,
    value,
    customStyles,
    withTiles,
    loadOptions,
    openMenuOnFocus,
    id,
    filterOption,
  } = props;

  const [height, setHeight] = useState<number | null>(null);
  const [openOnFocus, setOpenOnFocus] = useState(
    shouldOpenOnFocus !== undefined ? shouldOpenOnFocus : true,
  );
  const selectWrapperRef = useRef<any>(null);
  const selectRef = useRef<any>(null);

  const selectComponents = useMemo(() => CustomSelectComponents(props), []);

  useEffect(() => {
    if (forceOpen) {
      selectRef.current.select.focus();
    }
  }, [forceOpen]);

  useEffect(() => {
    if (forceFocus) {
      setInterval(() => {
        if (selectRef.current?.select) {
          selectRef.current.select.inputRef.focus();
        }
      }, 0);
    }
  }, [forceFocus]);

  const onMenuOpen = () => {
    setHeight(selectWrapperRef.current.offsetHeight);
    if (afterMenuOpen) {
      afterMenuOpen();
    }
  };

  const onMenuClose = () => {
    const menuEl = document.querySelector(`.menu`);

    const containerEl = menuEl?.parentElement;
    const clonedMenuEl = menuEl?.cloneNode(true) as HTMLElement;

    if (!clonedMenuEl) return;

    clonedMenuEl.classList.add("menu--close");
    clonedMenuEl.addEventListener("animationend", () => {
      containerEl?.removeChild(clonedMenuEl);
    });

    containerEl?.appendChild(clonedMenuEl!);
  };

  const onChangeHandler = (item: any) => {
    // apply onChange from parent
    onChange(item);

    // logic to not focus select when clearing
    // but select openMenuOnFocus props must be true
    // to clicking on placeholder works correctly
    if (isClearable) {
      if (item === null || (isMulti && item.length === 0)) {
        setOpenOnFocus(false);
        setTimeout(() => {
          selectRef.current.blur();
          setOpenOnFocus(true);
        }, 10);
      }
    }
  };

  const styles = customStyles || {
    control: (provided: any, state: any) => ({
      ...provided,
      boxShadow: state.isFocused && "0 0 0 1px #5d78ff",
    }),
    option: (styles: any) => {
      return {
        ...styles,
      };
    },
    multiValue: (styles: any) => {
      return {
        ...styles,
        backgroundColor: "transparent",
      };
    },
    multiValueLabel: (styles: any) => ({
      ...styles,
      color: "#74788d",
    }),
    multiValueRemove: (styles: any) => ({
      ...styles,
      color: "#D3D5E1",
      ":hover": {
        backgroundColor: "#F2F2F6",
        color: "#D3D5E1",
      },
    }),
  };

  return (
    <div className="ws-react-select-container">
      <div
        ref={selectWrapperRef}
        className={classNames("ws-react-select-wrapper", {
          "ws-react-select-wrapper--disabled": isDisabled,
        })}
      >
        <AsyncSelect
          ref={selectRef}
          classNamePrefix="ws-react-select"
          className={classNames("ws-react-select", {
            "ws-react-select--with-tiles": withTiles,
          })}
          value={value}
          hideSelectedOptions={false}
          onMenuOpen={onMenuOpen}
          onMenuClose={onMenuClose}
          openMenuOnFocus={
            openMenuOnFocus !== undefined ? openMenuOnFocus : openOnFocus
          }
          styles={styles}
          isDisabled={isDisabled}
          components={selectComponents}
          loadOptions={loadOptions}
          placeholder={placeholder}
          onSelectResetsInput={false}
          onBlurResetsInput={false}
          isClearable={false}
          filterOption={filterOption}
          isMulti={isMulti}
          defaultOptions
          isSearchable
          onChange={onChangeHandler}
          id={id}
        />
      </div>
      <span
        className="ws-react-select__height"
        style={{ height: `${height}px` }}
      />
    </div>
  );
}
